// useGmailThreadImport - import picked Gmail threads as first-class
// structured email_thread files.
//
// Each thread becomes ONE upload whose bytes are a JSON manifest
// (subject + per-message metadata/bodies + an attachments manifest).
//
// THE BROWSER ONLY STAGES. It holds the operator's Gmail OAuth token, so
// it is the only thing that can pull the attachment bytes - but the bytes
// never cross API Gateway (CLAUDE.md principle 9), and the browser never
// decides what an attachment BECOMES. It PUTs each attachment to a temp
// key and records `{s3_key, status:"staged"}`; the backend then promotes
// every staged attachment into a first-class upload through the SAME
// content-identity chokepoint the backend gmail_sync uses
// (`uploads.promote_staged_thread_attachments`, run before the thread's
// first extraction), rewriting each entry to `{status:"ingested",
// upload_id}`. That is what gives an imported attachment its own OCR,
// search indexing, facts and agent readability - which the old
// client-assembled `status:"extracted"` shape did not.
//
// Flow per thread:
//   1. getThreadProfileEmail        -> pulled_from
//   2. fetchThreadStructured        -> subject + messages + attachment refs
//   3. POST /uploads/initiate       -> upload_id + thread presigned_url
//   4. per attachment <= the size cap: POST /uploads/attachment/initiate ->
//      PUT bytes -> record s3_key + status="staged"; oversized ->
//      status="skipped"
//   5. PUT the assembled JSON to the thread presigned_url
//   6. POST /uploads/complete {kind:"email_thread"}
//
// The single-thread orchestration is factored into
// ``importGmailThread`` so the refresh path (useUploadActions) can
// re-pull a thread + attachments and PUT to a refresh presigned URL
// without duplicating the manifest-assembly logic.
import { useMutation, useQueryClient } from "react-query";
import { QK } from "../queryKeys";
import {
  GMAIL_ATTACHMENT_MAX_BYTES,
  gmailOversizeNote,
  fetchAttachmentBytes,
  fetchThreadStructured,
  getThreadProfileEmail,
  type GmailThreadStructured,
  type OnGmailRetry,
} from "../gmail";
import { useAgentsHttp } from "../useAgentsHttp";
import type { AgentsHttp } from "../useAgentsHttp";

const UPLOADS_LIST_KEY = QK.uploadsList;

// A persisted attachment-manifest entry as the BROWSER writes it.
// ``staged`` rows carry an s3_key pointing at parked bytes the backend
// has not promoted yet; skipped rows carry a human-readable note and no
// bytes. The backend rewrites every staged row in place to
// ``{status:"ingested", upload_id}`` (or re-files an image onto its
// message as an inline image), so this shape is transient - nothing but
// the promotion pass should ever read it.
export interface ThreadAttachmentManifestEntry {
  filename: string;
  mime_type: string;
  size_bytes: number;
  // The Gmail message id this part was found on. Preserved so the backend
  // extractor can attribute an OCR'd image attachment (a pasted
  // screenshot-of-text) to the message it belongs to and splice the
  // recovered text into that message's body.
  message_id?: string;
  // The part's Content-ID (brackets stripped). The plaintext body
  // references an inline image as `[cid:<content_id>]` at the exact spot
  // it sat; the backend replaces that placeholder with the OCR'd text.
  content_id?: string;
  s3_key?: string;
  status: "staged" | "skipped";
  note?: string;
}

// The full thread JSON document written as the email_thread file
// bytes. Matches the backend extractor's expected shape.
export interface ThreadManifest {
  thread_id: string;
  subject: string;
  pulled_from: string;
  messages: GmailThreadStructured["messages"];
  attachments: ThreadAttachmentManifestEntry[];
}

interface UploadInitiateResponse {
  upload_id: string;
  presigned_url: string;
  s3_key: string;
  expires_at: string;
  max_bytes: number;
}

interface AttachmentInitiateResponse {
  s3_key: string;
  presigned_url: string;
  expires_at: string;
  max_bytes: number;
}

export interface GmailThreadImportInput {
  project_id: string;
  thread_ids: string[];
  accessToken: string;
  pulled_by: string;
  pulled_by_name: string;
  // Fired before each transient-error backoff (rate-limit / 5xx) so the caller
  // can message the user mid-import instead of hard-failing the thread.
  onRetry?: OnGmailRetry;
}

export interface GmailThreadImportResult {
  imported: string[];
  errors: { thread_id: string; message: string }[];
}

// PUT a body to a presigned S3 URL, throwing a useful error on
// failure. Shared by the thread-JSON PUT + each attachment PUT.
const putToPresigned = async (
  url: string,
  body: BodyInit,
  contentType: string,
): Promise<void> => {
  const resp = await fetch(url, {
    method: "PUT",
    body,
    headers: { "content-type": contentType },
  });
  if (!resp.ok) {
    const detail = await resp.text();
    throw new Error(`S3 PUT failed (${resp.status}): ${detail.slice(0, 200)}`);
  }
};

// Pull every attachment <= the size cap into the TEMP key space (via
// /uploads/attachment/initiate + presigned PUT) and assemble the staged
// attachments manifest. Oversized attachments become skipped entries with
// no bytes. Returns the manifest array in source order.
//
// Exported because the refresh path (useUploadActions) must stage
// attachments identically - it previously had its own copy of this loop
// that silently dropped message_id + content_id, so a refreshed thread
// lost every attachment's attribution to its message and every inline
// image's [cid:] anchor.
export const pullAttachments = async (
  structured: GmailThreadStructured,
  parent_upload_id: string,
  project_id: string,
  accessToken: string,
  http: AgentsHttp,
  onRetry?: OnGmailRetry,
): Promise<ThreadAttachmentManifestEntry[]> => {
  const manifest: ThreadAttachmentManifestEntry[] = [];
  for (const att of structured.attachments) {
    if (att.size_bytes > GMAIL_ATTACHMENT_MAX_BYTES) {
      manifest.push({
        filename: att.filename,
        mime_type: att.mime_type,
        size_bytes: att.size_bytes,
        message_id: att.messageId,
        content_id: att.content_id,
        status: "skipped",
        note: gmailOversizeNote(),
      });
      continue;
    }
    const init = await http<AttachmentInitiateResponse>(
      "/uploads/attachment/initiate",
      {
        parent_upload_id,
        project_id,
        filename: att.filename,
        content_type: att.mime_type,
        size_bytes: att.size_bytes,
      },
    );
    const blob = await fetchAttachmentBytes(
      att.messageId,
      att.attachmentId,
      accessToken,
      att.mime_type,
      onRetry,
    );
    await putToPresigned(init.presigned_url, blob, att.mime_type);
    manifest.push({
      filename: att.filename,
      mime_type: att.mime_type,
      size_bytes: att.size_bytes,
      message_id: att.messageId,
      content_id: att.content_id,
      s3_key: init.s3_key,
      status: "staged",
    });
  }
  return manifest;
};

// Build the final thread JSON manifest from a structured thread +
// the pulled attachment manifest. Shared by import + refresh.
export const assembleThreadManifest = (
  structured: GmailThreadStructured,
  pulled_from: string,
  attachments: ThreadAttachmentManifestEntry[],
): ThreadManifest => ({
  thread_id: structured.thread_id,
  subject: structured.subject,
  pulled_from,
  messages: structured.messages,
  attachments,
});

// Import a single thread into a fresh upload. Returns the upload_id.
// Factored out of the hook so the refresh path can reuse the
// fetch-structured + pull-attachments + assemble-manifest pipeline.
export const importGmailThread = async ({
  thread_id,
  project_id,
  accessToken,
  pulled_by,
  pulled_by_name,
  pulled_from,
  http,
  onRetry,
}: {
  thread_id: string;
  project_id: string;
  accessToken: string;
  pulled_by: string;
  pulled_by_name: string;
  pulled_from: string;
  http: AgentsHttp;
  onRetry?: OnGmailRetry;
}): Promise<string> => {
  const structured = await fetchThreadStructured(thread_id, accessToken, onRetry);

  // The file name is the email subject (cleaned of whitespace, capped to
  // the 256-char original_name limit). Falls back to the thread id when a
  // thread has no subject.
  const subjectName =
    structured.subject.replace(/\s+/g, " ").trim().slice(0, 200) ||
    `Email thread ${thread_id.slice(0, 8)}`;

  const init = await http<UploadInitiateResponse>("/uploads/initiate", {
    project_id,
    original_name: subjectName,
    content_type: "application/json",
    source: "gmail",
    gmail_thread_id: thread_id,
    pulled_by,
    pulled_by_name,
    pulled_from,
  });

  const attachments = await pullAttachments(
    structured,
    init.upload_id,
    project_id,
    accessToken,
    http,
    onRetry,
  );

  const manifest = assembleThreadManifest(structured, pulled_from, attachments);
  await putToPresigned(
    init.presigned_url,
    JSON.stringify(manifest),
    "application/json",
  );

  await http("/uploads/complete", {
    upload_id: init.upload_id,
    kind: "email_thread",
  });

  return init.upload_id;
};

// Hook: import an array of picked thread ids for one project. Pass
// the project_id at construction time so the cache invalidation
// targets the right useUploadsList key. Returns a partial-success
// result ({imported, errors}) so one bad thread doesn't abort the
// whole batch - mirrors useUploadRefresh's shape.
export const useGmailThreadImport = (
  project_id: string | null | undefined,
) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<GmailThreadImportResult, Error, GmailThreadImportInput>(
    async (input) => {
      // One profile fetch per import covers every thread in the batch
      // (the access token is the same mailbox throughout the session).
      const pulled_from = await getThreadProfileEmail(
        input.accessToken,
        input.onRetry,
      );

      const imported: string[] = [];
      const errors: GmailThreadImportResult["errors"] = [];
      for (const thread_id of input.thread_ids) {
        try {
          await importGmailThread({
            thread_id,
            project_id: input.project_id,
            accessToken: input.accessToken,
            pulled_by: input.pulled_by,
            pulled_by_name: input.pulled_by_name,
            pulled_from,
            http,
            onRetry: input.onRetry,
          });
          imported.push(thread_id);
        } catch (e) {
          errors.push({
            thread_id,
            message: e instanceof Error ? e.message : String(e),
          });
        }
      }
      return { imported, errors };
    },
    {
      onSuccess: () => {
        if (project_id) {
          qc.invalidateQueries({ queryKey: [UPLOADS_LIST_KEY, project_id] });
        }
      },
    },
  );
};
